// Shared "manage custom columns" modal for the Password Vault and Company
// Information tables. Lets the agent add, rename, reorder and remove the
// agency-shared columns ([{ id, name }]). The parent owns persistence: on Save
// it receives the cleaned column list and writes it to agency settings.
//
// Deleting a column here only drops its NAME from the shared list; the values
// already typed into each row's customData stay on the rows (keyed by id) and
// simply stop showing — so an accidental delete + re-add with the same id would
// bring them back. We warn about that in the UI copy.
//
// EL TIPO SE ELIGE AL CREARLA, y se puede cambiar después (Lissette 2026-09-10:
// *"que cuando se cree la columna [esté] la opción de que sea escrito, o un drop
// down que se vaya creando a medida que se escribe, o un check"*). Los tres tipos
// y qué significan están en `crm-app/customColumns.js`.

import { useState } from 'react';
import { Columns as Columns3, Plus, Trash2, X, ArrowUp, ArrowDown } from 'lucide-react';
import { Modal } from './ui.jsx';
import { genColumnId, normalizeColumns, tipoDeColumna } from '../customColumns.js';
import { ColumnasParaRecuperar } from './ColumnasParaRecuperar.jsx';

const inputStyle = { width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.5rem .65rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, width: 'auto', flexShrink: 0, padding: '.5rem .4rem', fontSize: '.78rem', cursor: 'pointer' };

// Cómo se lee cada tipo. Cada uno lleva delante el dibujo de lo que va a salir en
// la casilla — la pluma, el desplegable y la marca —, porque el nombre a secas no
// dice qué va a aparecer en la tabla. El aviso de arriba explica que la lista se
// llena sola; aquí no cabe.
function nombresDeTipo(es) {
  return [
    { value: 'text', label: es ? '✏️ Escrito' : '✏️ Text' },
    { value: 'list', label: es ? '▾ Lista' : '▾ List' },
    { value: 'check', label: es ? '☑ Casilla' : '☑ Checkbox' },
  ];
}

// `modulo` (opcional): las tablas cuyas casillas guarda /custom-columns
// (renewals, onboarding, commissions). Con él, la ventana ofrece recuperar las
// columnas borradas que todavía tienen datos (E-391).
export default function CustomColumnsManager({ columns = [], lang = 'es', title, onClose, onSave, allowPhotoOption = false, modulo = null }) {
  const es = lang === 'es';
  const [cols, setCols] = useState(() => normalizeColumns(columns));
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('text');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const tipos = nombresDeTipo(es);

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    if (cols.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      setErr(es ? 'Ya existe una columna con ese nombre.' : 'A column with that name already exists.');
      return;
    }
    if (cols.length >= 30) { setErr(es ? 'Máximo 30 columnas.' : 'Maximum 30 columns.'); return; }
    setErr(null);
    setCols((p) => [...p, { id: genColumnId(), name, type: newType }]);
    setNewName('');
    // El tipo NO se resetea: quien está creando tres casillas seguidas las crea
    // seguidas, y volver a "Escrito" cada vez le haría elegirlo tres veces.
  };
  const rename = (id, name) => setCols((p) => p.map((c) => (c.id === id ? { ...c, name } : c)));
  const setTipo = (id, type) => setCols((p) => p.map((c) => (c.id === id ? { ...c, type } : c)));
  // Las opciones se escriben separadas por coma. La caja es NO controlada
  // (defaultValue): si se pintara desde el array, la coma que se acaba de teclear
  // desaparecería en cuanto se parte, y no se podría escribir la siguiente.
  const setOpciones = (id, texto) => setCols((p) => p.map((c) => (
    c.id === id ? { ...c, options: String(texto || '').split(',').map((s) => s.trim()).filter(Boolean) } : c
  )));
  const togglePhotos = (id) => setCols((p) => p.map((c) => (c.id === id ? { ...c, allowPhotos: !c.allowPhotos } : c)));
  const remove = (id) => setCols((p) => p.filter((c) => c.id !== id));
  const move = (idx, dir) => setCols((p) => {
    const next = p.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return p;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async () => {
    setErr(null);
    const cleaned = normalizeColumns(cols);
    setSaving(true);
    try {
      await onSave(cleaned);
      onClose();
    } catch (e) {
      setErr(e?.message || (es ? 'No se pudo guardar.' : 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title || (es ? 'Columnas personalizadas' : 'Custom columns')} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem', minWidth: 360 }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5, display: 'flex', gap: '.5rem', alignItems: 'flex-start' }}>
          <Columns3 size={16} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }} />
          <span>{es
            ? 'Agrega columnas extra a esta tabla. Aparecen en todas las filas; cada fila guarda su propio valor. Elige cómo se llena cada una: escrito a mano, una lista que se va armando sola con lo que el equipo escribe, o una casilla para marcar. Borrar una columna oculta sus valores pero no los borra de inmediato.'
            : 'Add extra columns to this table. They show on every row; each row keeps its own value. Pick how each one is filled in: free text, a list that builds itself from what the team types, or a checkbox. Deleting a column hides its values but does not erase them immediately.'}</span>
        </div>

        {/* Existing columns */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem', maxHeight: 320, overflowY: 'auto' }}>
          {cols.length === 0 && (
            <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1rem', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 9 }}>
              {es ? 'Aún no hay columnas personalizadas.' : 'No custom columns yet.'}
            </div>
          )}
          {cols.map((c, i) => { const tipo = tipoDeColumna(c); return (
            <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title={es ? 'Subir' : 'Move up'}
                    style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--c-border2,#1f3f60)' : 'var(--c-text3,#45688a)', padding: 0, display: 'flex' }}><ArrowUp size={13} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === cols.length - 1} title={es ? 'Bajar' : 'Move down'}
                    style={{ background: 'none', border: 'none', cursor: i === cols.length - 1 ? 'default' : 'pointer', color: i === cols.length - 1 ? 'var(--c-border2,#1f3f60)' : 'var(--c-text3,#45688a)', padding: 0, display: 'flex' }}><ArrowDown size={13} /></button>
                </div>
                <input value={c.name} onChange={(e) => rename(c.id, e.target.value.slice(0, 60))}
                  placeholder={es ? 'Nombre de la columna' : 'Column name'} style={inputStyle} />
                <select value={tipo} onChange={(e) => setTipo(c.id, e.target.value)} style={selectStyle}
                  title={es ? 'Cómo se llena esta columna' : 'How this column is filled in'}>
                  {tipos.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {/* La camarita solo tiene sentido donde se escribe: una casilla
                    marcable o un desplegable no llevan foto pegada. */}
                {allowPhotoOption && tipo === 'text' && (
                  <label title={es ? 'Permitir pegar/subir fotos en esta columna' : 'Allow pasting/uploading photos in this column'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: '.72rem', fontWeight: 700, color: c.allowPhotos ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', cursor: 'pointer', userSelect: 'none', border: `1px solid ${c.allowPhotos ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, borderRadius: 8, padding: '.4rem .55rem' }}>
                    <input type="checkbox" checked={!!c.allowPhotos} onChange={() => togglePhotos(c.id)} style={{ margin: 0 }} /> 📷
                  </label>
                )}
                <button type="button" onClick={() => remove(c.id)} title={es ? 'Quitar' : 'Remove'}
                  style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.45rem .5rem', color: 'var(--c-red,#ff3d6b)', cursor: 'pointer', flexShrink: 0 }}>
                  <Trash2 size={14} />
                </button>
              </div>
              {/* Las opciones son OPCIONALES: la lista se llena sola con lo que el
                  equipo va escribiendo. Esto es solo para dejar unas puestas desde
                  el primer día, cuando todavía no hay nada escrito. */}
              {tipo === 'list' && (
                <input defaultValue={(c.options || []).join(', ')}
                  onChange={(e) => setOpciones(c.id, e.target.value)}
                  placeholder={es ? 'Opciones de arranque, separadas por coma (opcional)' : 'Starter options, comma-separated (optional)'}
                  title={es ? 'La lista se va llenando sola con lo que el equipo escriba en esta columna. Aquí puedes dejar algunas puestas de antemano.' : 'The list fills itself with whatever the team types in this column. Here you can pre-seed a few.'}
                  style={{ ...inputStyle, marginLeft: 22, width: 'calc(100% - 22px)', fontSize: '.78rem', padding: '.4rem .6rem' }} />
              )}
            </div>
          ); })}
        </div>

        {/* Add new */}
        <div style={{ display: 'flex', gap: '.5rem', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.7rem' }}>
          <input value={newName} onChange={(e) => setNewName(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder={es ? 'Nueva columna…' : 'New column…'} style={inputStyle} />
          {/* El tipo se elige AQUÍ, al crearla, que es lo que se pidió. También se
              puede cambiar después en la fila de arriba. */}
          <select value={newType} onChange={(e) => setNewType(e.target.value)} style={selectStyle}
            title={es ? 'Cómo se llenará esta columna' : 'How this column will be filled in'}>
            {tipos.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button type="button" onClick={add}
            style={{ background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '.45rem .8rem', color: 'var(--c-cyan,#00d4ff)', fontSize: '.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <Plus size={14} /> {es ? 'Agregar' : 'Add'}
          </button>
        </div>

        {modulo && (
          <ColumnasParaRecuperar modulo={modulo} lang={lang} yaEnLaLista={new Set(cols.map((c) => c.id))}
            onRecuperar={(col) => {
              if (cols.some((c) => c.name.toLowerCase() === col.name.toLowerCase())) {
                setErr(es ? 'Ya existe una columna con ese nombre.' : 'A column with that name already exists.');
                return;
              }
              if (cols.length >= 30) { setErr(es ? 'Máximo 30 columnas.' : 'Maximum 30 columns.'); return; }
              setErr(null);
              setCols((p) => [...p, col]);
            }} />
        )}

        {err && <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" onClick={save} disabled={saving}
            style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? (es ? 'Guardando...' : 'Saving...') : (es ? 'Guardar' : 'Save')}</button>
        </div>
      </div>
    </Modal>
  );
}
